import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Download, Sparkles, ShieldCheck, Ruler } from "lucide-react";
import { BrandImage } from "@/lib/brand";

/**
 * /prep-guidelines — clinical preparation reference for dentists.
 *
 * One section per restoration type. Each section has:
 *   - a `<BrandImage>` slot keyed by `slug` so platform admins can swap in a
 *     5-10s MP4 clinical demo (or a photo) via Admin → Branding.
 *   - sectioned prep specs (margin / reduction / taper / turnaround) sourced
 *     from the lab's 2026 Preparation Guidelines deck.
 *   - a "do / don't" list distilled from the deck's clinical notes.
 *
 * The page degrades gracefully when no video has been uploaded — the
 * `<BrandImage>` falls back to a bundled prep photo in /public/prep/.
 */

const SECTIONS = [
    {
        slug: "prep_anterior_crown",
        anchor: "anterior-crown",
        name: "Anterior Crown",
        category: "Crown",
        tagline: "Single-unit aesthetics · incisal-edge layering",
        fallback: "/prep/image1.png",
        spec: {
            margin: "1.0 mm shoulder, light-chamfer acceptable supragingival",
            facial: "1.2–1.5 mm reduction (facial)",
            incisal: "2.0 mm incisal reduction (functional clearance)",
            lingual: "1.0 mm lingual (concavity preserved)",
            taper: "6–10° total convergence",
            finish: "Smooth, rounded internal line angles · no sharp transitions",
        },
        dos: [
            "Keep margins supragingival or equigingival where aesthetic line allows.",
            "Maintain consistent axial reduction depth — depth-cutter burs help.",
            "Photograph the prep + adjacent shade tab on the day of impression.",
        ],
        donts: [
            "Avoid feather-edge margins on anterior crowns — they fracture at the cervix.",
            "Don't over-reduce the lingual concavity; preserves resistance form.",
            "No sharp internal line angles — they create stress concentrations.",
        ],
        turnaround: "10-14 working hours · same-day option available for Doha clinics",
    },
    {
        slug: "prep_posterior_crown",
        anchor: "posterior-crown",
        name: "Posterior Crown",
        category: "Crown",
        tagline: "Functional reduction for molars and pre-molars",
        fallback: "/prep/image2.png",
        spec: {
            margin: "1.0 mm chamfer or rounded shoulder",
            occlusal: "1.5–2.0 mm reduction (clearance for opposing cusps)",
            axial: "1.0–1.2 mm reduction",
            functional_cusp: "Additional 0.5 mm bevel on functional cusp",
            taper: "6–10° total convergence · taller wall = lower taper",
            finish: "Definite margin with chamfer bur · no J-margins",
        },
        dos: [
            "Verify occlusal clearance with the patient in centric AND lateral excursions.",
            "Add a functional-cusp bevel — prevents perforation through the crown.",
            "Capture the entire margin in one impression sweep — no double-tear retakes.",
        ],
        donts: [
            "Don't leave an undercut on the buccal of upper molars — alters the path of insertion.",
            "Avoid sub-gingival margins deeper than 1 mm — periodontium reacts.",
            "Don't skip the band-on-band impression check for distal margins.",
        ],
        turnaround: "6-9 working hours",
    },
    {
        slug: "prep_onlay",
        anchor: "onlay",
        name: "Onlay",
        category: "Indirect",
        tagline: "Cusp-covering conservative restoration",
        fallback: "/prep/image3.png",
        spec: {
            cavosurface: "90° butt-joint at the cavosurface — NO bevel",
            isthmus: "≥2 mm width at the isthmus",
            depth: "2.0–3.0 mm pulpal floor depth",
            cusp_coverage: "≥1.5 mm reduction over the covered cusp",
            walls: "Diverging cavity walls · 6–10° outward",
            finish: "Smooth, polished cavity surfaces — no undercuts",
        },
        dos: [
            "Cover any cusp with an undermined enamel rod > 2 mm wide.",
            "Round all internal line angles — onlays love stress relief.",
            "Use a wedge + matrix during impression to capture the proximal box.",
        ],
        donts: [
            "Don't bevel the cavosurface — onlay relies on a butt joint for marginal strength.",
            "Avoid leaving sharp axiopulpal angles — they fracture in firing.",
            "No subgingival proximal margins below 2 mm — moisture control fails.",
        ],
        turnaround: "6-8 working hours",
    },
    {
        slug: "prep_inlay",
        anchor: "inlay",
        name: "Inlay",
        category: "Indirect",
        tagline: "Intra-coronal · no cusp coverage",
        fallback: "/prep/image4.png",
        spec: {
            cavosurface: "90° butt-joint — bevel is contraindicated",
            isthmus: "≥1.5 mm width",
            depth: "1.5–2.5 mm pulpal floor depth",
            taper: "6–8° diverging walls (towards the occlusal)",
            box_form: "Proximal box with definite gingival floor",
            finish: "Smooth walls, no undercuts, axiopulpal line angle rounded",
        },
        dos: [
            "Keep the cavity confined to the affected area — preserve enamel rim.",
            "Verify minimum 1.5 mm isthmus or upgrade to an onlay.",
            "Use rubber dam for the impression — moisture warps the proximal box.",
        ],
        donts: [
            "Don't bevel the cavosurface margin.",
            "Avoid undercuts — they prevent seating of the milled inlay.",
            "No occlusal contact directly on the cavosurface — recipe for marginal chipping.",
        ],
        turnaround: "6-8 working hours",
    },
    {
        slug: "prep_bridge",
        anchor: "bridge",
        name: "Bridge",
        category: "Multi-unit",
        tagline: "Abutment prep + pontic design considerations",
        fallback: "/prep/image11.png",
        spec: {
            abutment_margin: "1.0 mm chamfer or shoulder (matched on both abutments)",
            abutment_reduction: "Standard crown reduction (1.0–1.5 mm axial · 2.0 mm occlusal)",
            pontic: "Modified ridge-lap for posterior · ovate for anterior aesthetics",
            connector: "Minimum 9 mm² cross-section (zirconia · 3-unit)",
            taper: "6–10° matched between abutments — common path of insertion",
            finish: "Both abutments must be reduced symmetrically — uneven prep = uneven cementation",
        },
        dos: [
            "Achieve common path of insertion BEFORE impression — verify with surveyor.",
            "Round-off undercuts on both abutments at the same time.",
            "Photograph the pontic site after preparation so the lab can design ovate emergence.",
        ],
        donts: [
            "Don't accept >15° divergence between abutments — bridge won't seat.",
            "Avoid placing the connector below the gingival margin.",
            "No span longer than 3 units in zirconia without confirming with the lab first.",
        ],
        turnaround: "12-16 working hours",
    },
    {
        slug: "prep_veneer",
        anchor: "veneer",
        name: "Veneer",
        category: "Aesthetic",
        tagline: "0.3–0.5 mm laminate · enamel-preserving",
        fallback: "/prep/image6.png",
        spec: {
            facial: "0.3–0.5 mm reduction · depth-cutter recommended",
            incisal: "1.0–1.5 mm overlap (palatal chamfer)",
            margin: "Feather-edge OR 0.3 mm chamfer · supragingival",
            interproximal: "Just break contact · stay within enamel",
            taper: "Minimal — preserve as much enamel as possible",
            finish: "Smooth · highly polished · enamel rod-ends sealed",
        },
        dos: [
            "Use depth-cutter burs on the facial — protects against over-reduction.",
            "Keep the palatal margin above the centric contact whenever possible.",
            "Photograph the prep with retraction cord placed — captures the margin.",
        ],
        donts: [
            "Don't go through the enamel into dentine — bond strength collapses.",
            "Avoid placing the margin in a heavy occlusal contact area.",
            "No supragingival 'L-shaped' incisal preparation — fractures during cementation.",
        ],
        turnaround: "10-14 working hours",
    },
];

const CATEGORY_COLORS = {
    Crown:       { chipBg: "bg-cyan-50",      chipFg: "text-cyan-800",      accent: "bg-cyan-500" },
    Indirect:    { chipBg: "bg-emerald-50",   chipFg: "text-emerald-800",   accent: "bg-emerald-500" },
    "Multi-unit": { chipBg: "bg-violet-50",   chipFg: "text-violet-800",    accent: "bg-violet-500" },
    Aesthetic:   { chipBg: "bg-rose-50",      chipFg: "text-rose-800",      accent: "bg-rose-500" },
};

export default function PrepGuidelines() {
    const [active, setActive] = useState(SECTIONS[0].anchor);

    return (
        <main className="min-h-screen bg-[#FAFBFC]" data-testid="prep-guidelines-page">
            {/* Hero */}
            <section className="bg-[#0B1120] text-white">
                <div className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
                    <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-cyan-400 mb-3">· Clinical reference · 2026 edition</div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                        Preparation guidelines
                    </h1>
                    <p className="mt-5 text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">
                        Margin design, reduction, and try-in protocol for every restoration type we manufacture.
                        Skim before the prep visit, share with new associates, and tap any section to see a video
                        demonstration (when published by the lab).
                    </p>
                    <div className="mt-6 flex flex-wrap items-center gap-3 text-[13px]">
                        <Link to="/workflow" data-testid="prep-cta-send-case"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-cyan-500 text-slate-900 font-semibold hover:bg-cyan-400 transition">
                            Send a case <ArrowRight size={14} />
                        </Link>
                        <Link to="/for-clinics" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white transition">
                            See full catalog <ArrowUpRight size={14} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Sticky in-page nav */}
            <nav className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="prep-section-nav">
                <div className="max-w-6xl mx-auto px-5 md:px-8 flex gap-1 overflow-x-auto py-3 scrollbar-thin">
                    {SECTIONS.map((s) => (
                        <a
                            key={s.anchor}
                            href={`#${s.anchor}`}
                            onClick={() => setActive(s.anchor)}
                            data-testid={`prep-nav-${s.anchor}`}
                            className={`shrink-0 px-3 py-1.5 rounded-full text-[12.5px] font-medium transition ${
                                active === s.anchor
                                    ? "bg-slate-900 text-white"
                                    : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            {s.name}
                        </a>
                    ))}
                </div>
            </nav>

            {/* Sections */}
            <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 lg:py-20 space-y-20 lg:space-y-28">
                {SECTIONS.map((s, idx) => {
                    const cat = CATEGORY_COLORS[s.category] || CATEGORY_COLORS.Crown;
                    return (
                        <section key={s.anchor} id={s.anchor} className="scroll-mt-20" data-testid={`prep-section-${s.anchor}`}>
                            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${cat.chipBg} ${cat.chipFg} text-[10.5px] font-mono-tr tracking-wider uppercase mb-3`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${cat.accent}`} /> {s.category}
                            </div>
                            <div className="flex items-baseline gap-4 mb-2">
                                <span className="font-mono-tr text-[11px] text-slate-400 tracking-wider">{String(idx + 1).padStart(2, "0")}</span>
                                <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-slate-900">{s.name}</h2>
                            </div>
                            <p className="text-slate-600 text-[15px] leading-relaxed max-w-2xl">{s.tagline}</p>

                            <div className="mt-8 grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                                {/* Media — admin-editable. Photo OR autoplay-muted-loop video */}
                                <div className="rounded-2xl overflow-hidden bg-slate-100 ring-1 ring-slate-200 aspect-video" data-testid={`prep-media-${s.anchor}`}>
                                    <BrandImage
                                        slug={s.slug}
                                        fallback={s.fallback}
                                        alt={`${s.name} preparation demonstration`}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                {/* Prep spec */}
                                <div>
                                    <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="h-8 w-8 rounded-lg bg-slate-900 text-cyan-400 grid place-items-center"><Ruler size={15} /></div>
                                            <div className="font-display text-lg text-slate-900 tracking-tight">Prep specifications</div>
                                        </div>
                                        <dl className="divide-y divide-slate-100">
                                            {Object.entries(s.spec).map(([k, v]) => (
                                                <div key={k} className="grid grid-cols-[120px_1fr] gap-3 py-2.5 first:pt-0 last:pb-0">
                                                    <dt className="text-[11px] font-mono-tr tracking-wider uppercase text-slate-500">{k.replace(/_/g, " ")}</dt>
                                                    <dd className="text-[13.5px] text-slate-800 leading-snug">{v}</dd>
                                                </div>
                                            ))}
                                        </dl>
                                        <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2 text-[12.5px] text-slate-600">
                                            <ShieldCheck size={13} className="text-emerald-600" />
                                            Turnaround: <span className="text-slate-900 font-medium">{s.turnaround}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Do / Don't */}
                            <div className="mt-8 grid md:grid-cols-2 gap-5">
                                <div className="rounded-2xl bg-emerald-50/60 ring-1 ring-emerald-200 p-5" data-testid={`prep-dos-${s.anchor}`}>
                                    <div className="font-mono-tr text-[11px] tracking-[0.18em] uppercase text-emerald-700 mb-3 flex items-center gap-1.5">
                                        <Sparkles size={11} /> Do
                                    </div>
                                    <ul className="space-y-2 text-[13.5px] text-slate-800 leading-snug">
                                        {s.dos.map((line) => (
                                            <li key={line} className="flex gap-2.5">
                                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 mt-2 shrink-0" />
                                                <span>{line}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="rounded-2xl bg-rose-50/60 ring-1 ring-rose-200 p-5" data-testid={`prep-donts-${s.anchor}`}>
                                    <div className="font-mono-tr text-[11px] tracking-[0.18em] uppercase text-rose-700 mb-3">Don&apos;t</div>
                                    <ul className="space-y-2 text-[13.5px] text-slate-800 leading-snug">
                                        {s.donts.map((line) => (
                                            <li key={line} className="flex gap-2.5">
                                                <span className="h-1.5 w-1.5 rounded-full bg-rose-500 mt-2 shrink-0" />
                                                <span>{line}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </section>
                    );
                })}

                {/* Footer CTA */}
                <section className="rounded-3xl bg-[#0B1120] text-white p-8 md:p-12 text-center" data-testid="prep-footer-cta">
                    <div className="text-[10.5px] font-mono-tr tracking-[0.22em] uppercase text-cyan-400 mb-3">· Need a remake or a same-day case?</div>
                    <h2 className="font-display text-3xl md:text-4xl tracking-tight">Send us your prep — we&apos;ll handle the rest.</h2>
                    <p className="mt-3 text-white/70 max-w-xl mx-auto text-[14.5px] leading-relaxed">
                        Same-day crowns for Doha clinics, 24-48h dispatch across the GCC. Free pickup over QAR 800.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3 text-[13px]">
                        <Link to="/workflow" data-testid="prep-footer-send-case"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-cyan-500 text-slate-900 font-semibold hover:bg-cyan-400 transition">
                            Submit a case <ArrowRight size={14} />
                        </Link>
                        <a href="/api/docs/clinic-guide" target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white/85 hover:bg-white/5 transition">
                            <Download size={14} /> Clinic playbook
                        </a>
                    </div>
                </section>
            </div>
        </main>
    );
}
